import {
  EnvironmentProviders,
  ErrorHandler,
  Injectable,
  InjectionToken,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * A deploy replaces the hashed lazy chunks, and a tab that is already open keeps asking
 * for the ones its index.html was built against. The CDN keeps the previous build around
 * for a grace window (see SpaFrontendStack in wetaxi-cdk-lib), but a rollback, a very old
 * tab, or a chunk that outlived the window still ends in a failed dynamic import — and
 * because CloudFront answers a missing object with index.html, the browser reports it as
 * a module/MIME failure rather than a 404.
 *
 * The fix is always the same: reload, which fetches the current index.html (served
 * no-store) and with it the chunk names that actually exist.
 */
const STALE_BUILD_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /expected a javascript module script/i,
  /loading chunk \S+ failed/i,
  /chunkloaderror/i,
];

/** A reload that does not fix it is a broken build, not a stale one — don't spin on it. */
const RELOAD_COOLDOWN_MS = 5 * 60_000;

const RELOAD_MARKER = 'wetaxi.stale-build-reload';

/** Indirected so specs can assert on the reload without navigating the test runner. */
export const RELOAD_PAGE = new InjectionToken<() => void>('RELOAD_PAGE', {
  providedIn: 'root',
  factory: () => (): void => location.reload(),
});

export function isStaleBuildError(error: unknown): boolean {
  if (error == null) {
    return false;
  }

  const wrapped = (error as { rejection?: unknown }).rejection;
  if (wrapped != null && wrapped !== error) {
    return isStaleBuildError(wrapped);
  }

  const { name, message } = error as { name?: unknown; message?: unknown };
  const text = typeof error === 'string' ? error : `${name ?? ''} ${message ?? ''}`;

  return STALE_BUILD_PATTERNS.some((pattern) => pattern.test(text));
}

@Injectable({ providedIn: 'root' })
export class StaleBuildReloadService {
  private readonly router = inject(Router);
  private readonly reloadPage = inject(RELOAD_PAGE);

  /** Wires the two places a failed chunk import surfaces in a zoneless app. */
  listen(): void {
    window.addEventListener('unhandledrejection', (event) => {
      if (this.recover(event.reason)) {
        event.preventDefault();
      }
    });

    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => this.recover(event.error));
  }

  /** True when the error was a missing chunk and a reload has been triggered for it. */
  recover(error: unknown): boolean {
    if (!isStaleBuildError(error)) {
      return false;
    }

    const last = this.readMarker();
    if (last !== null && Date.now() - last < RELOAD_COOLDOWN_MS) {
      console.error('[stale-build] a chunk is still missing after reloading — not reloading again', error);
      return false;
    }

    this.writeMarker(Date.now());
    this.reloadPage();
    return true;
  }

  private readMarker(): number | null {
    try {
      const raw = sessionStorage.getItem(RELOAD_MARKER);
      const at = raw === null ? NaN : Number(raw);
      return Number.isFinite(at) ? at : null;
    } catch {
      // Storage can be unavailable (private mode, blocked cookies); reloading is still right.
      return null;
    }
  }

  private writeMarker(at: number): void {
    try {
      sessionStorage.setItem(RELOAD_MARKER, String(at));
    } catch {
      // Without the marker the cooldown cannot be enforced — better than never recovering.
    }
  }
}

/**
 * Where an error goes once the stale-build check has ruled out a reload fixing it. Angular
 * takes a single `ErrorHandler`, so a reporter cannot simply be provided alongside this one —
 * it would replace it and take the chunk recovery with it. `provideSentry()` fills this in;
 * with nothing registered (local dev, specs) the built-in console handler stands in.
 */
export const REPORTING_ERROR_HANDLER = new InjectionToken<ErrorHandler>('REPORTING_ERROR_HANDLER');

@Injectable()
export class StaleBuildErrorHandler extends ErrorHandler {
  private readonly staleBuild = inject(StaleBuildReloadService);
  private readonly reporter = inject(REPORTING_ERROR_HANDLER, { optional: true });

  override handleError(error: unknown): void {
    if (this.staleBuild.recover(error)) {
      return;
    }
    if (this.reporter) {
      this.reporter.handleError(error);
      return;
    }
    super.handleError(error);
  }
}

/** Recovers from lazy chunks that a deploy has taken away, by reloading once. */
export function provideStaleBuildReload(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ErrorHandler, useClass: StaleBuildErrorHandler },
    provideAppInitializer(() => inject(StaleBuildReloadService).listen()),
  ]);
}
